<script>
/**
 * @component AIConsultationDrawer
 * @description Drawer lateral de asistencia IA en la atención clínica.
 *              Recibe la transcripción, la envía a la API y auto-aplica todas las
 *              sugerencias directamente en los campos del formulario.
 *              El médico puede ver el resumen y re-analizar si lo desea.
 * @prop {boolean} visible - Controla visibilidad del drawer
 * @prop {string} transcription - Texto transcripto de la consulta
 * @prop {string} patientId - ID del paciente
 * @prop {string} appointmentId - ID de la atención
 * @emits close - Al cerrar el drawer
 * @emits apply-vitals - Sugerencias de signos vitales
 * @emits apply-medications - Sugerencias de medicamentos
 * @emits apply-imaging - Sugerencias de estudios de imagen
 * @emits apply-lab - Sugerencias de laboratorio
 * @emits apply-summary - Resumen/síntomas/diagnóstico (auto-emitido al analizar)
 * @example
 *   AIConsultationDrawer(
 *     :visible="drawerVisible"
 *     :transcription="transcriptionText"
 *     :patientId="patient._id"
 *     :appointmentId="appointment._id"
 *     @close="drawerVisible = false"
 *     @apply-vitals="onApplyVitals"
 *     @apply-medications="onApplyMedications"
 *     @apply-imaging="onApplyImaging"
 *     @apply-lab="onApplyLab"
 *     @apply-summary="onApplySummary"
 *   )
 */
import aiService from "@/services/ai";

export default {
  name: "AIConsultationDrawer",

  props: {
    /** Controla visibilidad del drawer */
    visible: { type: Boolean, default: false },
    /** Texto transcripto de la consulta */
    transcription: { type: String, default: "" },
    /** ID del paciente */
    patientId: { type: String, default: "" },
    /** ID de la atención */
    appointmentId: { type: String, default: "" },
  },

  data() {
    return {
      loading: false,
      result: null,
      error: null,
      /** Indica si el resultado ya fue aplicado al formulario */
      applied: false,
    };
  },

  watch: {
    /**
     * @description Cuando el drawer se abre con transcripción disponible, analiza automáticamente
     */
    visible(val) {
      if (val && this.transcription && !this.result) {
        this.analyze();
      }
    },
  },

  methods: {
    /**
     * @description Envía la transcripción a la API, carga el resultado y lo aplica automáticamente
     */
    async analyze() {
      if (!this.transcription.trim()) {
        this.error = "No hay transcripción para analizar.";
        return;
      }
      try {
        this.loading = true;
        this.error = null;
        this.result = null;
        this.applied = false;
        const response = await aiService.analyzeConsultation(
          this.transcription
        );
        this.result = response.data || response;
        console.log("Resultado IA:", JSON.stringify(this.result));
        this.autoApplyAll();
      } catch (e) {
        this.error =
          e?.response?.data?.message || "Error al analizar la consulta con IA.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Aplica automáticamente todas las secciones del resultado al formulario
     */
    autoApplyAll() {
      if (!this.result) return;
      if (
        this.result.summary ||
        this.result.symptoms?.length ||
        this.result.diagnosis
      ) {
        this.$emit("apply-summary", {
          summary: this.result.summary,
          symptoms: this.result.symptoms,
          diagnosis: this.result.diagnosis,
        });
      }
      if (this.result.vitals && this.result.vitals.length) {
        this.$emit("apply-vitals", this.result.vitals);
      }
      if (this.result.imaging && this.result.imaging.length) {
        this.$emit("apply-imaging", this.result.imaging);
      }
      if (this.result.lab && this.result.lab.length) {
        this.$emit("apply-lab", this.result.lab);
      }
      if (this.result.medications && this.result.medications.length) {
        this.$emit("apply-medications", this.result.medications);
      }
      this.applied = true;
      this.$message.success("Sugerencias de IA aplicadas al formulario");
    },

    /**
     * @description Cierra el drawer
     */
    close() {
      this.$emit("close");
    },

    /**
     * @description Reinicia el análisis borrando el resultado anterior
     */
    reanalyze() {
      this.result = null;
      this.error = null;
      this.applied = false;
      this.analyze();
    },
  },
};
</script>

<template lang="pug">
transition(name="ai-drawer-slide")
  .ai-drawer(v-if="visible")
    //- Overlay
    .ai-drawer__overlay(@click="close")

    //- Panel
    .ai-drawer__panel
      //- Header
      .ai-drawer__header
        .ai-drawer__header-left
          .ai-drawer__header-icon
            i.el-icon-magic-stick
          .ai-drawer__header-text
            h3.ai-drawer__title Asistencia IA
            p.ai-drawer__subtitle Sugerencias basadas en la transcripción
        button.ai-drawer__close(@click="close")
          i.el-icon-close

      //- Body
      .ai-drawer__body
        //- Loading
        .ai-drawer__loading(v-if="loading")
          i.el-icon-loading
          span Analizando consulta...

        //- Error
        .ai-drawer__error(v-else-if="error")
          i.el-icon-warning-outline
          span {{ error }}
          el-button(size="mini" type="text" @click="reanalyze") Reintentar

        //- Resultado (solo visualización — se aplicó automáticamente)
        template(v-else-if="result")
          .ai-drawer__applied-badge(v-if="applied")
            i.el-icon-check
            span Sugerencias aplicadas al formulario

          //- Resumen
          .ai-section
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Resumen de la consulta
            .ai-section__body
              p.ai-section__text {{ result.summary }}
              template(v-if="result.symptoms && result.symptoms.length")
                p.ai-section__sublabel Síntomas detectados
                .ai-section__tags
                  span.ai-tag(v-for="s in result.symptoms" :key="s") {{ s }}

          //- Diagnóstico
          .ai-section
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Diagnóstico sugerido
            .ai-section__body
              p.ai-section__text {{ result.diagnosis || 'Sin sugerencia' }}

          //- Signos vitales
          .ai-section(v-if="result.vitals && result.vitals.length")
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Signos vitales sugeridos
            .ai-section__body
              .ai-vital-row(v-for="v in result.vitals" :key="v.name")
                span.ai-vital-name {{ v.name }}
                span.ai-vital-value {{ v.value }} {{ v.unit }}

          //- Estudios de imagen
          .ai-section(v-if="result.imaging && result.imaging.length")
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Estudios de imagen
            .ai-section__body
              .ai-study-row(v-for="s in result.imaging" :key="s.name")
                span.ai-study-name {{ s.name }}
                span.ai-study-reason {{ s.reason }}

          //- Estudios de laboratorio
          .ai-section(v-if="result.lab && result.lab.length")
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Laboratorio
            .ai-section__body
              .ai-study-row(v-for="s in result.lab" :key="s.name")
                span.ai-study-name {{ s.name }}
                span.ai-study-reason {{ s.reason }}

          //- Medicamentos
          .ai-section(v-if="result.medications && result.medications.length")
            .ai-section__header
              .ai-section__title-group
                span.ai-section__title Medicamentos sugeridos
            .ai-section__body
              .ai-med-row(v-for="m in result.medications" :key="m.name")
                span.ai-med-name {{ m.name }}
                .ai-med-detail
                  span {{ m.dose }}
                  span(v-if="m.frequency") · {{ m.frequency }}
                  span(v-if="m.duration") · {{ m.duration }}

          //- Sin sugerencias adicionales
          .ai-drawer__empty(
            v-if="!(result.vitals && result.vitals.length) && !(result.imaging && result.imaging.length) && !(result.lab && result.lab.length) && !(result.medications && result.medications.length)"
          )
            i.el-icon-info-outline
            span No se detectaron sugerencias adicionales en la transcripción.

      //- Footer simplificado
      .ai-drawer__footer
        el-button(size="small" @click="reanalyze" :disabled="loading") Re-analizar
        el-button(size="small" @click="close") Cerrar
</template>

<style lang="scss" scoped>
.ai-drawer {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: flex-end;

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 45, 94, 0.35);
  }

  &__panel {
    position: relative;
    width: 420px;
    max-width: 95vw;
    height: 100%;
    background: #fff;
    display: flex;
    flex-direction: column;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__header-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #e6f9f9;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 18px;
      color: #009b7d;
    }
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: $text-secondary-new;
    margin: 0;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    color: $text-secondary-new;
    font-size: 18px;
    padding: 4px;
    transition: color 0.2s;

    &:hover {
      color: $navy;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 20px;
    color: $text-secondary-new;
    font-size: 14px;

    i {
      font-size: 28px;
      color: $teal;
    }
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    color: #dc2626;
    font-size: 13px;
    text-align: center;

    i {
      font-size: 24px;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    color: $text-secondary-new;
    font-size: 13px;

    i {
      font-size: 16px;
    }
  }

  &__applied-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    color: #16a34a;
    font-size: 13px;
    font-weight: 600;

    i {
      font-size: 14px;
    }
  }

  &__footer {
    padding: 14px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
  }
}

/* Secciones */
.ai-section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  &__title-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: $navy;
  }

  &__applied {
    font-size: 11px;
    font-weight: 600;
    color: #16a34a;
  }

  &__body {
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__text {
    font-size: 13px;
    color: $text-primary-new;
    line-height: 1.5;
    margin: 0;
  }

  &__sublabel {
    font-size: 11px;
    font-weight: 600;
    color: $text-secondary-new;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 4px 0 0;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.ai-tag {
  background: #e6f9f9;
  color: #0e7490;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
}

.ai-vital-row,
.ai-study-row,
.ai-med-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.ai-vital-name,
.ai-study-name,
.ai-med-name {
  font-weight: 600;
  color: $text-primary-new;
  flex: 1;
}

.ai-vital-value {
  color: $teal;
  font-weight: 600;
  white-space: nowrap;
}

.ai-study-reason {
  color: $text-secondary-new;
  font-size: 12px;
  flex: 1;
}

.ai-med-detail {
  color: $text-secondary-new;
  font-size: 12px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

/* Transición */
.ai-drawer-slide-enter-active,
.ai-drawer-slide-leave-active {
  transition: opacity 0.25s ease;

  .ai-drawer__panel {
    transition: transform 0.25s ease;
  }
}

.ai-drawer-slide-enter,
.ai-drawer-slide-leave-to {
  opacity: 0;

  .ai-drawer__panel {
    transform: translateX(100%);
  }
}
</style>
